import React, { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { getToken, API } from "@/lib/api";
import { toast } from "sonner";

export default function VoiceButton({ onText, label = "Voice", testid = "voice-btn" }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Mic not supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = handleStop;
      rec.start();
      recRef.current = rec;
      setState("recording");
    } catch (e) {
      toast.error("Mic permission denied.");
    }
  };

  const stop = () => {
    if (recRef.current && state === "recording") {
      recRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const handleStop = async () => {
    setState("uploading");
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    const fd = new FormData();
    fd.append("file", blob, "voice.webm");
    try {
      const res = await fetch(`${API}/transcribe`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.text) throw new Error("Empty transcription");
      onText?.(data.text);
      toast.success("Transcribed.");
    } catch (e) {
      toast.error(`Transcription failed: ${e.message}`);
    } finally {
      setState("idle");
    }
  };

  const click = () => {
    if (state === "idle") start();
    else if (state === "recording") stop();
  };

  const isRec = state === "recording";
  const isUp = state === "uploading";

  return (
    <button
      type="button"
      data-testid={testid}
      onClick={click}
      disabled={isUp}
      className={`inline-flex items-center gap-2 border px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors ${
        isRec
          ? "border-signal-red text-signal-red bg-signal-red/10 animate-pulse"
          : "border-white/15 text-zinc-300 hover:border-signal-yellow hover:text-signal-yellow"
      } disabled:opacity-40`}
    >
      {isUp ? <Loader2 size={12} className="animate-spin" /> : isRec ? <Square size={12} /> : <Mic size={12} />}
      {isUp ? "Transcribing..." : isRec ? "Stop" : label}
    </button>
  );
}
